import { Flex, Grid, Text, Tooltip } from '@chakra-ui/react';
import { useMemo } from 'react';
import { IconButton } from 'ui-components/dist/Button';
import { TrashIcon } from 'ui-components/dist/TrashIcon';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components/ContentCardPlatformIcon';
import { Feature } from '~/components/Feature';
import { IconBox } from '~/components/IconBox';
import { ALL_SOCIAL_PLATFORM_TYPE, FEATURE_FLAGS } from '~/constants';
import { ConnectedPlatformsType } from '~/screens/ManagePlatforms';
import { useEditPlatformsStore } from '~/screens/ManagePlatforms/EditPlatformsModal_DEPRECATED';
import { useRemovePlatformStore } from '~/screens/ManagePlatforms/RemovePlatformModal';
import { formatAbbr } from '~/utils';
import { convertPxToRems } from '~/utils-ts';

export const Platform = ({
  platform,
}: {
  platform: ConnectedPlatformsType;
}) => {
  const {
    creator,
    closeModal: closeEditPlatform,
    connectedPlatforms,
  } = useEditPlatformsStore();
  const {
    setIsModalOpen: showRemoveConfirmationDialog,
    setPlatform,
    setCreatorId,
  } = useRemovePlatformStore();

  const handleRemove = (platform: ALL_SOCIAL_PLATFORM_TYPE) => {
    closeEditPlatform();
    showRemoveConfirmationDialog(true);
    setPlatform(platform);
    setCreatorId(creator?.id || '');
  };

  const getTooltipLabel = useMemo(() => {
    if (connectedPlatforms.length === 1)
      return 'Talent must have at least one social account';

    if (creator?.isOnCampaign)
      return 'Cannot delete when the creator is associated with a campaign';

    if (platform.status === 'connected' || platform.status === 'disconnected')
      return 'Contact Support for assistance with removing this platform';

    return null;
  }, [connectedPlatforms, creator, platform.status]);

  return (
    <Grid
      background="primary.25"
      padding={2}
      paddingBottom={4}
      paddingLeft={4}
      minHeight={convertPxToRems(85)}
      borderRadius="lg"
      gap={1}
    >
      <Flex gap={4} height={8} alignItems="center">
        <Flex gap={1}>
          <IconBox
            icon={
              <ContentCardPlatformIcon
                boxSize={4}
                platform={platform.platform as ALL_SOCIAL_PLATFORM_TYPE}
              />
            }
          />
          <Text color="primary.400" fontSize="md" fontWeight={400}>
            @{platform.handle}
          </Text>
        </Flex>
        <Feature name={FEATURE_FLAGS.foamOffPlatformRemoveSocials}>
          <Tooltip
            hasArrow
            label={getTooltipLabel}
            textAlign="center"
            width={64}
            paddingX={4}
            paddingY={2}
            borderRadius="lg"
            background="primary.700"
            placement="top"
            fontSize="sm"
            fontWeight="400"
          >
            <IconButton
              aria-label="Remove platform"
              variant="transparent"
              size="sm"
              isDisabled={
                connectedPlatforms.length === 1 ||
                creator?.isOnCampaign ||
                platform.status === 'connected' ||
                platform.status === 'disconnected'
              }
              onClick={() =>
                handleRemove(platform.platform as ALL_SOCIAL_PLATFORM_TYPE)
              }
              icon={<TrashIcon boxSize={4} color="primary.700" />}
            />
          </Tooltip>
        </Feature>
      </Flex>
      <Text
        color="primary.700"
        fontSize="2xl"
        fontWeight="500"
        lineHeight="25px"
      >
        {formatAbbr(platform.count)}
      </Text>
    </Grid>
  );
};
